<template>
    <UPopover :content="{ align: 'end' }" @update:open="onOpen">
        <UChip :show="unread > 0" :text="unread > 9 ? '9+' : String(unread)" size="xl" color="primary" inset>
            <UButton
                color="neutral"
                variant="ghost"
                size="sm"
                icon="i-lucide-bell"
                :aria-label="$t('notifications.title')"
            />
        </UChip>

        <template #content>
            <div class="w-80 max-w-[calc(100vw-2rem)]">
                <div class="flex items-center justify-between gap-2 px-3 py-2 border-b border-default">
                    <p class="font-mono text-[11px] uppercase tracking-widest text-muted">{{ $t('notifications.title') }}</p>
                    <UButton
                        v-if="unread > 0"
                        size="xs"
                        color="neutral"
                        variant="ghost"
                        :label="$t('notifications.mark_all')"
                        @click="markAll"
                    />
                </div>
                <div class="max-h-96 overflow-y-auto">
                    <p v-if="!items.length" class="px-3 py-6 text-center text-sm text-muted">{{ $t('notifications.empty') }}</p>
                    <button
                        v-for="n in items"
                        :key="n.id"
                        type="button"
                        class="w-full text-left px-3 py-2 border-b border-default last:border-b-0 hover:bg-elevated"
                        @click="openItem(n)"
                    >
                        <span class="flex items-start gap-2">
                            <span v-if="!n.read" class="mt-1.5 size-2 shrink-0 rounded-full bg-primary" :aria-label="$t('notifications.unread')" />
                            <span class="min-w-0">
                                <span class="block text-sm font-medium text-highlighted">{{ n.title }}</span>
                                <span v-if="n.body" class="block text-xs text-muted">{{ n.body }}</span>
                                <span class="block text-[11px] text-dimmed">{{ formatDateTime(n.created_at) }}</span>
                            </span>
                        </span>
                    </button>
                </div>
            </div>
        </template>
    </UPopover>
</template>

<script lang="ts" setup>
    import { useIntervalFn } from '@vueuse/core'
    import { computed, onMounted, ref } from 'vue'
    import { useRouter } from 'vue-router'

    import { formatDateTime } from '@/libs/dayjs'
    import { useCompanyStore } from '@/stores/company'
    import { useFetchStore } from '@/stores/fetch'
    import type { INotification } from '@/types/User'

    const fetchStore   = useFetchStore()
    const companyStore = useCompanyStore()
    const router       = useRouter()

    const items  = ref<INotification[]>([])
    const unread = computed(() => items.value.filter((n) => !n.read).length)

    const load = async () => {
        const { statusCode, data } = await fetchStore.fetch('/me/notifications').get().json<INotification[]>()
        if (statusCode.value === 200 && data.value) items.value = data.value
    }

    const markRead = async (ids: string[]) => {
        await fetchStore.fetch('/me/notifications/read').post({ ids }).json()
        await load()
    }

    const markAll = () => markRead([])

    const onOpen = (open: boolean) => {
        if (open) void load()
    }

    const openItem = async (n: INotification) => {
        if (!n.read) await markRead([n.id])
        if (n.company_id && n.company_id !== companyStore.companyId) await companyStore.select(n.company_id)
        if (n.link) await router.push(n.link)
    }

    onMounted(load)
    useIntervalFn(load, 60000)
</script>
